import "./productVideos.scss";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useGetVendorsDropdown } from "hooks/useVendors";
import {
    adminListProductVideos,
    adminUpdateProductVideo,
    adminDeleteProductVideo,
    vendorListProductVideos,
    vendorDeleteProductVideo,
} from "services/productVideos";
import ProductVideoRow from "./ProductVideoRow";
import ProductVideoUploadModal from "./ProductVideoUploadModal";
import ProductVideoEditModal from "./ProductVideoEditModal";

const TABS = [
    { value: "pending", label: "قيد المراجعة" },
    { value: "approved", label: "معتمدة" },
    { value: "rejected", label: "مرفوضة" },
    { value: "", label: "الكل" },
];

const VENDOR_TABS = [
    { value: "", label: "الكل" },
    { value: "pending", label: "قيد المراجعة" },
    { value: "approved", label: "معتمدة" },
    { value: "rejected", label: "مرفوضة" },
];

/**
 * Single screen that flips between admin moderation view and vendor "my
 * videos" view based on `adminInfo.role`. Same pattern the rest of the app
 * uses for /products and /orders.
 */
const ProductVideos = () => {
    const adminInfo = useSelector((state) => state.auth.userData);
    const isVendorScope = adminInfo?.role === "vendor";
    const scope = isVendorScope ? "vendor" : "admin";
    const navigate = useNavigate();

    const { vendors } = useGetVendorsDropdown();

    const [status, setStatus] = useState(isVendorScope ? "" : "pending");
    const [vendorFilter, setVendorFilter] = useState("");
    const [q, setQ] = useState("");
    const [data, setData] = useState({ rows: [], count: 0, pendingCount: 0 });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [uploadOpen, setUploadOpen] = useState(false);
    const [editTarget, setEditTarget] = useState(null);

    const tabs = isVendorScope ? VENDOR_TABS : TABS;

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = { limit: 50 };
            if (status) params.status = status;
            if (!isVendorScope) {
                if (vendorFilter) params.vendorId = vendorFilter;
                if (q) params.q = q;
            }
            const lister = isVendorScope ? vendorListProductVideos : adminListProductVideos;
            const response = await lister(params);
            setData({
                rows: response.rows || [],
                count: response.count || 0,
                pendingCount: response.pendingCount || 0,
            });
        } catch (err) {
            setError(err.message || "فشل تحميل الفيديوهات");
        } finally {
            setLoading(false);
        }
    }, [isVendorScope, status, vendorFilter, q]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleApprove = async (id) => {
        try {
            await adminUpdateProductVideo(id, { status: "approved" });
            fetchData();
        } catch (err) {
            window.alert(err.message || "فشل الاعتماد");
        }
    };

    const handleReject = async (id, notes) => {
        try {
            await adminUpdateProductVideo(id, {
                status: "rejected",
                adminNotes: notes || null,
            });
            fetchData();
        } catch (err) {
            window.alert(err.message || "فشل الرفض");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("هل أنت متأكد من حذف الفيديو؟")) return;
        try {
            const remover = isVendorScope ? vendorDeleteProductVideo : adminDeleteProductVideo;
            await remover(id);
            fetchData();
        } catch (err) {
            window.alert(err.message || "فشل الحذف");
        }
    };

    const tabPendingBadge = useMemo(
        () => (isVendorScope ? null : data.pendingCount),
        [isVendorScope, data.pendingCount],
    );

    return (
        <div className="product-videos">
            <div className="toolbar-card">
                <header className="header">
                    <h1>{isVendorScope ? "فيديوهاتي" : "فيديوهات المنتجات"}</h1>
                    <button className="upload-btn" onClick={() => setUploadOpen(true)}>
                        + رفع فيديو
                    </button>
                </header>

                <nav className="tabs">
                    {tabs.map((t) => (
                        <button
                            key={t.value || "all"}
                            className={status === t.value ? "active" : ""}
                            onClick={() => setStatus(t.value)}
                            type="button"
                        >
                            {t.label}
                            {t.value === "pending" && tabPendingBadge ? (
                                <span className="count-badge">{tabPendingBadge}</span>
                            ) : null}
                        </button>
                    ))}
                </nav>

                {!isVendorScope ? (
                    <div className="filters">
                        <select
                            value={vendorFilter}
                            onChange={(e) => setVendorFilter(e.target.value)}
                        >
                            <option value="">كل المطاعم</option>
                            {vendors.map((v) => (
                                <option key={v.id} value={v.id}>
                                    {v.name}
                                </option>
                            ))}
                        </select>
                        <input
                            type="text"
                            placeholder="بحث في عنوان المنتج..."
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                        />
                    </div>
                ) : null}
            </div>

            {loading ? <p className="loading">...جار التحميل</p> : null}
            {error ? <p className="empty">{error}</p> : null}
            {!loading && data.rows.length === 0 ? <p className="empty">لا توجد فيديوهات</p> : null}

            <ul className="list">
                {data.rows.map((video) => (
                    <ProductVideoRow
                        key={video.id}
                        video={video}
                        scope={scope}
                        onApprove={() => handleApprove(video.id)}
                        onReject={(notes) => handleReject(video.id, notes)}
                        onDelete={() => handleDelete(video.id)}
                        onEdit={() => setEditTarget(video)}
                        onOpenProduct={
                            video.product?.id
                                ? () => navigate(`/products/${video.product.id}`)
                                : null
                        }
                    />
                ))}
            </ul>

            {uploadOpen ? (
                <ProductVideoUploadModal
                    scope={scope}
                    currentUser={adminInfo}
                    onClose={() => setUploadOpen(false)}
                    onUploaded={fetchData}
                />
            ) : null}

            {editTarget ? (
                <ProductVideoEditModal
                    scope={scope}
                    video={editTarget}
                    currentUser={adminInfo}
                    onClose={() => setEditTarget(null)}
                    onSaved={fetchData}
                />
            ) : null}
        </div>
    );
};

export default ProductVideos;
